import * as React from "react";

type Card = {
  id: string;
  name: string;
  role: string;
  text: string;
  avatar: string;
  handle?: string;
};

const DEFAULTS: Card[] = [
  {
    id: "1",
    name: "Aman Verma",
    role: "Canada PR Applicant",
    text:
      "Cleared TEF in my first attempt! The classes are structured around the actual TEF format, and mock tests really built my confidence for the listening and speaking sections.",
    avatar:
      "https://framerusercontent.com/images/UVKHq11tZnbjw2YQYP8hdL4C0Lw.jpg",
    handle: "@Aman",
  },
  {
    id: "2",
    name: "Neha Bhatia",
    role: "IT Professional",
    text:
      "I was preparing for TEF while working full-time. The flexible batch timings and personalised feedback from tutors helped me achieve CLB 7 comfortably.",
    avatar: "https://framerusercontent.com/images/zVt9OlIjfYTSElDcAUhMqON4E.jpg",
    handle: "@Neha",
  },
  {
    id: "3",
    name: "Rohit S.",
    role: "Immigration Candidate",
    text:
      "The teachers actually understand the TEF exam inside out. Every session focused on what matters — strategies, speed, and pronunciation. Got my CLB 9 score!",
    avatar: "https://framerusercontent.com/images/VeynQiuTiVEyWCsEEfVvRxCs.jpg",
    handle: "@Rohit",
  },
  {
    id: "4",
    name: "Priya Nair",
    role: "Marketing Executive",
    text:
      "I had tried learning French before but never felt confident. With Online French Skool’s TEF course, I finally understood grammar and improved my speaking drastically.",
    avatar:
      "https://framerusercontent.com/images/UVKHq11tZnbjw2YQYP8hdL4C0Lw.jpg",
    handle: "@Priya",
  },
  {
    id: "5",
    name: "Karan Patel",
    role: "Engineer (Canada PR)",
    text:
      "The mock interviews and speaking drills were game-changers. I could clearly see my improvement week by week. Cleared TEF Canada within 3 months!",
    avatar: "https://framerusercontent.com/images/zVt9OlIjfYTSElDcAUhMqON4E.jpg",
    handle: "@Karan",
  },
  {
    id: "6",
    name: "Sana Sheikh",
    role: "Healthcare Worker",
    text:
      "What I loved most was the one-on-one guidance and TEF practice portal. The teachers genuinely want you to succeed. Highly recommend for anyone targeting Canada PR.",
    avatar: "https://framerusercontent.com/images/VeynQiuTiVEyWCsEEfVvRxCs.jpg",
    handle: "@Sana",
  },
];

export default function TestimonialsMarquee({
  cards = DEFAULTS,
  speed = 28, // lower = faster (seconds for a full loop)
}: {
  cards?: Card[];
  speed?: number;
}) {
  // duplicate the array so the loop can cross the seam invisibly
  const loop = React.useMemo(() => [...cards, ...cards], [cards]);

  return (
    <div className="relative mx-auto w-full max-w-[1600px] px-0 md:pb-24 pb-10 bg-[#FBFCF2]">
      {/* viewport with mask/fades */}
      <div className="relative overflow-hidden [--fade:0px] md:[--fade:120px]"
        style={{
          WebkitMaskImage:
            "linear-gradient(to right, transparent 0, #000 var(--fade), #000 calc(100% - var(--fade)), transparent 100%)",
          maskImage:
            "linear-gradient(to right, transparent 0, #000 var(--fade), #000 calc(100% - var(--fade)), transparent 100%)",
        }}
      >
        {/* infinite strip */}
        <ul
          className="flex gap-6 z-0 will-change-transform "
          style={{
            // the strip is wider than the viewport and moves left forever
            animation: `marquee ${speed}s linear infinite`,
          }}
        >
          {loop.map((c, idx) => (
            <li
              key={`${c.id}-${idx}`}
              className="h-full shrink-0 mb-4"
              style={{ width: 380,}}
            >
              <article className="relative h-full rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5">
                <div className="mb-4 flex items-center gap-3 border-[#D1D0D6] border-b-[1px] pb-3 ">
                  <img
                    src={c.avatar}
                    alt=""
                    className="h-10 w-10 rounded-full object-cover ring-1 ring-white/70 shadow"
                    draggable={false}
                  />
                  <div>
                    <p className="font-medium text-neutral-900">{c.name}</p>
                    <p className="text-sm text-neutral-500">{c.role}</p>
                  </div>
                </div>

                {/* stars line like ref */}
                <div className="pb-6">
                  <svg width="100" height="20" viewBox="0 0 100 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.0251 8.33329L10.0001 1.66663L7.97508 8.33329H1.66675L6.81675 12.0083L4.85841 18.3333L10.0001 14.425L15.1501 18.3333L13.1917 12.0083L18.3334 8.33329H12.0251Z" fill="#C2C100"/>
                  <path d="M32.0251 8.33329L30.0001 1.66663L27.9751 8.33329H21.6667L26.8167 12.0083L24.8584 18.3333L30.0001 14.425L35.1501 18.3333L33.1917 12.0083L38.3334 8.33329H32.0251Z" fill="#C2C100"/>
                  <path d="M52.0251 8.33329L50.0001 1.66663L47.9751 8.33329H41.6667L46.8167 12.0083L44.8584 18.3333L50.0001 14.425L55.1501 18.3333L53.1917 12.0083L58.3334 8.33329H52.0251Z" fill="#C2C100"/>
                  <path d="M72.0251 8.33329L70.0001 1.66663L67.9751 8.33329H61.6667L66.8167 12.0083L64.8584 18.3333L70.0001 14.425L75.1501 18.3333L73.1917 12.0083L78.3334 8.33329H72.0251Z" fill="#C2C100"/>
                  <path d="M92.0251 8.33329L90.0001 1.66663L87.9751 8.33329H81.6667L86.8167 12.0083L84.8584 18.3333L90.0001 14.425L95.1501 18.3333L93.1917 12.0083L98.3334 8.33329H92.0251Z" fill="#C2C100"/>
                  </svg>
                </div>


                <p className="text-neutral-700">{c.text}</p>

                {/* subtle top divider like in ref */}
                <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-neutral-200/70" />
              </article>
            </li>
          ))}
        </ul>
      </div>

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); } /* because we doubled the list */
        }
      `}</style>
    </div>
  );
}
